import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) {
    throw new Error(body?.message || 'EOIP request failed');
  }
  return body.data;
}

export type EoipIntelligenceCard = {
  id: string;
  kind: string;
  priority: string;
  headline: string;
  why: string;
  recommendation?: string | null;
  confidence: number;
  domain: string;
  actionCta?: string | null;
};

export type EoipDashboard = {
  pulse: {
    score: number;
    statusLabel: string;
    dimensions?: Record<string, number>;
    computedAt?: string;
  } | null;
  cards: EoipIntelligenceCard[];
  signals: unknown[];
  predictions: unknown[];
  opportunities: unknown[];
  recommendations: Array<Record<string, unknown>>;
  correlations: unknown[];
  degrade?: { jobs_disabled?: boolean; openai_unavailable?: boolean };
  refreshed_at?: string;
};

export const eoipService = {
  getDashboard: () => unwrap<EoipDashboard>(api.get('/eoip/dashboard')),
  getFeed: () =>
    unwrap<{ cards: EoipIntelligenceCard[]; signals: unknown[]; refreshed_at: string }>(
      api.get('/eoip/feed')
    ),
  getPulse: () => unwrap<Record<string, unknown>>(api.get('/eoip/pulse')),
  getPredictions: () => unwrap<unknown[]>(api.get('/eoip/predictions')),
  getRecommendations: () => unwrap<unknown[]>(api.get('/eoip/recommendations')),
  getOpportunities: () => unwrap<unknown[]>(api.get('/eoip/opportunities')),
  getAttention: () => unwrap<{ text: string }>(api.get('/eoip/attention')),
  getExecutiveBrief: (role = 'CEO') =>
    unwrap<Record<string, unknown> | null>(
      api.get(`/eoip/executive-brief?role=${encodeURIComponent(role)}`)
    ),
  getWatchlists: () => unwrap<unknown[]>(api.get('/eoip/watchlists')),
  createWatchlist: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/eoip/watchlists', payload)),
  upsertSubscription: (payload: Record<string, unknown>) =>
    unwrap<{ id: string }>(api.post('/eoip/subscriptions', payload)),
  postActionFeedback: (payload: {
    recommendationId: string;
    feedback: 'accepted' | 'rejected' | 'ignored';
    notes?: string;
  }) => unwrap<{ id: string }>(api.post('/eoip/action-feedback', payload)),
  runScenario: (payload: { scenarioType: string; params?: Record<string, unknown> }) =>
    unwrap<unknown>(api.post('/eoip/scenarios', payload)),
  listScenarios: () => unwrap<unknown[]>(api.get('/eoip/scenarios')),
};
